import { map, Observable } from 'rxjs';

import { TranslatedCountryCode } from '@fcom/common-booking/components/pax-details/pax-details-form-group.component';
import { add, sortBy, subtract, TimeUnit, toISO8601 } from '@fcom/core/utils';
import { SelectOption, TripFilterDateRange } from '@fcom/common';
import { FinnairCabinClass } from '@fcom/dapi/api/models';
import { GlobalBookingTravelClass } from '@fcom/core/interfaces';

export const parsePhoneNumber = (countryCode: string | undefined, nationalNumber: string | undefined): string => {
  if (!countryCode || !nationalNumber) {
    return '';
  }
  const prefix = countryCode.includes('|') ? countryCode.split('|').pop() : countryCode;
  return `+${prefix}${nationalNumber}`;
};

export const getPhonePrefixOptions = (
  countryCodes$: Observable<TranslatedCountryCode[]>
): Observable<SelectOption[]> => {
  return countryCodes$.pipe(
    map((countryCodes: TranslatedCountryCode[]) => {
      return sortBy(
        countryCodes.map((cc) => ({
          name: `${cc.name} (+${cc.phonePrefix})`,
          value: `${cc.countryCode}|${cc.phonePrefix}`,
        })),
        (option) => option.name
      );
    })
  );
};

export enum FilterDateRangeKey {
  ALL = 'all',
  NEXT_WEEK = 'next-week',
  NEXT_MONTH = 'next-month',
  NEXT_YEAR = 'next-year',
  PAST_WEEK = 'past-week',
  PAST_MONTH = 'past-month',
  PAST_YEAR = 'past-year',
}

export const getFilterDateRange = (key: FilterDateRangeKey): TripFilterDateRange => {
  const today = new Date();
  switch (key) {
    case FilterDateRangeKey.NEXT_WEEK:
      return {
        fromDate: toISO8601(today),
        toDate: toISO8601(add(today, 7, TimeUnit.day)),
      };
    case FilterDateRangeKey.NEXT_MONTH:
      return {
        fromDate: toISO8601(today),
        toDate: toISO8601(add(today, 1, TimeUnit.month)),
      };
    case FilterDateRangeKey.NEXT_YEAR:
      return {
        fromDate: toISO8601(today),
        toDate: toISO8601(add(today, 1, TimeUnit.year)),
      };
    case FilterDateRangeKey.PAST_YEAR:
      return {
        fromDate: toISO8601(subtract(today, 1, TimeUnit.year)),
        toDate: toISO8601(subtract(today, 1, TimeUnit.day)),
        allFlightsInRange: true,
      };
    case FilterDateRangeKey.PAST_MONTH:
      return {
        fromDate: toISO8601(subtract(today, 1, TimeUnit.month)),
        toDate: toISO8601(subtract(today, 1, TimeUnit.day)),
        allFlightsInRange: true,
      };
    case FilterDateRangeKey.PAST_WEEK:
      return {
        fromDate: toISO8601(subtract(today, 7, TimeUnit.day)),
        toDate: toISO8601(subtract(today, 1, TimeUnit.day)),
        allFlightsInRange: true,
      };
    case FilterDateRangeKey.ALL:
    default:
      return {
        fromDate: toISO8601(today),
        toDate: undefined,
      };
  }
};

export const mapFinnairCabinClassToGlobalBookingTravelClass = (
  travelClass: FinnairCabinClass
): GlobalBookingTravelClass => {
  switch (travelClass) {
    case FinnairCabinClass.ECONOMY:
      return GlobalBookingTravelClass.ECONOMY;
    case FinnairCabinClass.ECOPREMIUM:
      return GlobalBookingTravelClass.ECOPREMIUM;
    case FinnairCabinClass.BUSINESS:
      return GlobalBookingTravelClass.BUSINESS;
    default:
      return GlobalBookingTravelClass.MIXED;
  }
};
